import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from './UserContext';
import api from '../utils/api';

interface Clinic {
  ClinicID: number;
  ClinicName: string;
  CompanyID: number;
  Address?: string;
  ClinicNPI?: string;
  ClinicCity?: string;
  ClinicState?: string;
  phone_number?: string;
}

interface ClinicsContextType {
  clinics: Clinic[];
  loading: boolean;
  error: string | null;
  refetchClinics: () => Promise<void>;
  lastRefreshed: Date | null;
}

const ClinicsContext = createContext<ClinicsContextType | undefined>(undefined);

export const ClinicsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useUser();
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchClinics = useCallback(async () => {
    // Check if user data exists in context
    if (!user) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/clinics');
      setClinics(response.data);
      setLastRefreshed(new Date());
      console.log('Clinics data refreshed at', new Date().toLocaleTimeString());
    } catch (err: any) {
      // Don't set error if it's an auth error (will be handled by api interceptor)
      if (err.response?.status !== 401) {
        setError(err.response?.data?.message || 'Failed to fetch clinics');
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // Check if user data exists in context
    if (user) {
      fetchClinics();
    }
  }, [fetchClinics, user]);

  return (
    <ClinicsContext.Provider value={{ 
      clinics, 
      loading, 
      error, 
      refetchClinics: fetchClinics,
      lastRefreshed
    }}>
      {children}
    </ClinicsContext.Provider>
  );
};

export const useClinics = () => {
  const context = useContext(ClinicsContext);
  if (!context) {
    throw new Error('useClinics must be used within a ClinicsProvider');
  }
  return context;
}; 